import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, MessageCircle } from "lucide-react";
import { CONTACT } from "@/constants/contact";

const SWAP_MS = 3800;
const pill = "relative flex items-center justify-center gap-2 min-w-[156px] bg-terracotta text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-lg active:scale-95 transition-transform";
const flip = {
  initial: { rotateX: -90, opacity: 0 },
  animate: { rotateX: 0, opacity: 1 },
  exit: { rotateX: 90, opacity: 0 },
  transition: { duration: 0.28, ease: "easeOut" },
};

// True once the hero's bottom edge is above the bubble's spot, so the bubble never sits on hero text.
const usePastHero = (selector) => {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const hero = document.querySelector(selector);
    if (!hero) { setPast(true); return undefined; }
    let raf = 0;
    const check = () => { raf = 0; setPast(hero.getBoundingClientRect().bottom < window.innerHeight - 96); };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [selector]);
  return past;
};

const Led = () => <span aria-hidden="true" className="bubble-led h-2 w-2 rounded-full bg-white" />;

// Phone-only floating bubble that flips between "Book online" and "Text Bryan" like a signal light.
export const FloatingBubble = ({ onBook, hidden, heroSelector }) => {
  const past = usePastHero(heroSelector);
  const [mode, setMode] = useState("book");
  const holdUntil = useRef(0); // a finger on the bubble freezes it so the tap does what it read
  useEffect(() => {
    const t = setInterval(() => {
      if (Date.now() > holdUntil.current) setMode((m) => (m === "book" ? "text" : "book"));
    }, SWAP_MS);
    return () => clearInterval(t);
  }, []);
  const hold = () => { holdUntil.current = Date.now() + 2500; };

  return (
    <AnimatePresence>
      {past && !hidden && (
        <motion.div
          key="bubble"
          data-testid="floating-bubble"
          data-mode={mode}
          initial={{ opacity: 0, y: 24, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          onPointerDown={hold}
          className="md:hidden fixed bottom-5 right-5 z-40 [perspective:600px]"
        >
          <span aria-hidden="true" className="bubble-beacon absolute inset-0 rounded-full bg-terracotta" />
          <AnimatePresence mode="wait" initial={false}>
            {mode === "book" ? (
              <motion.button key="book" {...flip} type="button" onClick={onBook} data-book-cta
                data-testid="book-online-bubble" aria-label="Book online" className={pill}>
                <Led /><CalendarCheck className="h-5 w-5" /> Book online
              </motion.button>
            ) : (
              <motion.a key="text" {...flip} href={CONTACT.smsHref}
                data-testid="text-bryan-bubble" aria-label="Text Bryan" className={pill}>
                <Led /><MessageCircle className="h-5 w-5" /> Text Bryan
              </motion.a>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
